/**
 * Pure best-effort lifecycle timeline for the Student card (Area 6, Req 16.6).
 *
 * While the canonical `StudentTimeline` feed is absent from the schema
 * (Backend_Timeline_Dependency), `deriveBestEffortTimeline` reconstructs events
 * purely from data already on `StudentDetailQuery`: agreement `date` → signed,
 * group presence → enrolled/moved, each payment `date` → paid, a payment's
 * linked invoice → invoice issued/linked, and `terminatedDate` → terminated.
 * Events are returned in reverse-chronological order. Decoupled from
 * React/Apollo so it is property-tested in isolation (design Property 12); when
 * the real feed lands, `ActivityTimeline` swaps the source with no UI change.
 */

/** Event category, derived from the event type (drives the category color). */
export type TimelineCategory =
  | 'signed'
  | 'paid'
  | 'moved'
  | 'terminated'
  | 'invoiceIssued'
  | 'invoiceLinked';

/** A single derived lifecycle event. `labelKey` is an i18next key (Req 16.7). */
export interface ITimelineEvent {
  id: string;
  /** ISO-ish date string as carried by the source data. */
  date: string;
  category: TimelineCategory;
  labelKey: string;
  /** Optional interpolation values for the label (e.g. group name, amount). */
  meta?: Record<string, string | number>;
}

/* --- Minimal structural input shapes (decoupled from generated types) --- */

interface ITimelineInvoice {
  number?: string | null;
}
interface ITimelinePayment {
  id?: string | null;
  date?: string | null;
  amountAgreement?: number | string | null;
  currency?: string | null;
  contribution?: { id?: string | null } | null;
  fakturowniaInvoice?: ITimelineInvoice | null;
}
interface ITimelineAgreement {
  id?: string | null;
  agreementTitle?: string | null;
  date?: string | null;
  terminated?: boolean | null;
  terminatedDate?: string | null;
  group?: { id?: string | null; name?: string | null } | null;
  payments?: ReadonlyArray<ITimelinePayment | null> | null;
}
export interface ITimelineStudent {
  agreements?: ReadonlyArray<ITimelineAgreement | null> | null;
}

const I18N_PREFIX = 'pages.studentDetail.timeline.events';

/** Stable epoch for reverse-chron sorting; unparseable dates sort last (oldest). */
const toTime = (date: string): number => {
  const t = Date.parse(date);
  return Number.isFinite(t) ? t : Number.NEGATIVE_INFINITY;
};

/* ------------------------------------------------------------------ */
/* Real backend feed adapter (TZ-3).                                   */
/* ------------------------------------------------------------------ */

/** Backend `TimelineEventTypeEnum` → our display category. */
const TYPE_TO_CATEGORY: Readonly<Record<string, TimelineCategory>> = {
  SIGNED: 'signed',
  PAID: 'paid',
  MOVED: 'moved',
  TERMINATED: 'terminated',
  INVOICE_ISSUED: 'invoiceIssued',
  INVOICE_LINKED: 'invoiceLinked',
};

/** One backend timeline node (loose, decoupled from generated types). */
interface IBackendTimelineNode {
  id?: string | null;
  type?: string | null;
  date?: string | null;
  /** `GenericScalar` — arbitrary JSON with snake_case keys. */
  meta?: Record<string, unknown> | null;
}
interface IBackendTimelineEdge {
  node?: IBackendTimelineNode | null;
}

const NAMED_ENTITIES: Readonly<Record<string, string>> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
};

/** `String.fromCodePoint` that returns the raw match on an out-of-range value. */
const safeFromCodePoint = (codePoint: number, fallback: string): string => {
  try {
    return String.fromCodePoint(codePoint);
  } catch {
    return fallback;
  }
};

/** One decode pass: hex (`&#x2F;`) → decimal (`&#47;`) → named (`&amp;`). */
const decodeOnce = (value: string): string =>
  value
    .replace(/&#x([0-9a-fA-F]+);/g, (whole, hex) =>
      safeFromCodePoint(parseInt(hex, 16), whole),
    )
    .replace(/&#(\d+);/g, (whole, dec) =>
      safeFromCodePoint(parseInt(dec, 10), whole),
    )
    .replace(
      /&(amp|lt|gt|quot|apos|nbsp);/g,
      (whole, name) => NAMED_ENTITIES[name] ?? whole,
    );

/**
 * Decode HTML entities the backend applies to `meta` string values.
 *
 * Fakturownia invoice numbers contain slashes (`18/11/2025`), and the backend
 * serialises them HTML-escaped (`18&#x2F;11&#x2F;2025`). `meta` carries
 * plain-text DATA, not markup, and React renders text literally (it does not
 * decode entities), so without this the operator sees the raw `&#x2F;`.
 *
 * Runs the decode pass repeatedly until the string stabilises (bounded) so a
 * DOUBLE-escaped value (`&amp;#x2F;` → `&#x2F;` → `/`) is fully resolved, not
 * just one layer. Decoding plain-text data for display is always safe here.
 */
export const decodeHtmlEntities = (value: string): string => {
  let current = value;
  for (let pass = 0; pass < 5; pass += 1) {
    const next = decodeOnce(current);
    if (next === current) break;
    current = next;
  }
  return current;
};

const str = (v: unknown): string =>
  v == null ? '' : decodeHtmlEntities(String(v));

/**
 * Build the i18n interpolation vars for a category from the backend `meta` JSON
 * (snake_case → our label placeholders). MOVED carries only `*_id`s (no group
 * name), so it interpolates nothing — its label is generic.
 */
const metaVarsFor = (
  category: TimelineCategory,
  meta: Record<string, unknown> | null | undefined,
): Record<string, string | number> => {
  const m = meta || {};
  switch (category) {
    case 'signed':
    case 'terminated':
      return { title: str(m.agreement_title) };
    case 'paid':
      return { amount: `${str(m.amount_agreement)} ${str(m.currency)}`.trim() };
    case 'invoiceIssued':
    case 'invoiceLinked':
      return { number: str(m.number) };
    default:
      return {};
  }
};

/**
 * Map the canonical backend timeline feed (`student.timeline.edges`) into the
 * same `ITimelineEvent[]` the panel renders (TZ-3). The backend `label` (RU
 * string) is intentionally ignored — we localize ourselves from `type` + `meta`.
 * Unknown event types are dropped. Events are returned reverse-chronologically.
 */
export const mapBackendTimeline = (
  edges: ReadonlyArray<IBackendTimelineEdge | null> | null | undefined,
): ITimelineEvent[] => {
  const events: ITimelineEvent[] = [];

  (edges || []).forEach((edge, index) => {
    const node = edge?.node;
    if (!node) return;
    const category = TYPE_TO_CATEGORY[node.type || ''];
    if (!category) return; // unknown type → skip

    events.push({
      id: node.id || `t${index}`,
      date: node.date || '',
      category,
      labelKey: `${I18N_PREFIX}.${category}`,
      meta: metaVarsFor(category, node.meta),
    });
  });

  return events.sort((a, b) => toTime(b.date) - toTime(a.date));
};

/**
 * Derive the best-effort event list from a student's already-loaded data.
 *
 * Returns only events derivable from present fields, each tagged with its
 * category, ordered non-increasingly by date (most recent first).
 */
export const deriveBestEffortTimeline = (
  student: ITimelineStudent | null | undefined,
): ITimelineEvent[] => {
  const events: ITimelineEvent[] = [];

  (student?.agreements || []).forEach((agreement, agreementIndex) => {
    if (!agreement) return;
    const aId = agreement.id || `a${agreementIndex}`;
    const title = agreement.agreementTitle || '';

    if (agreement.date) {
      events.push({
        id: `${aId}-signed`,
        date: agreement.date,
        category: 'signed',
        labelKey: `${I18N_PREFIX}.signed`,
        meta: { title },
      });

      if (agreement.group?.name) {
        events.push({
          id: `${aId}-enrolled`,
          date: agreement.date,
          category: 'moved',
          labelKey: `${I18N_PREFIX}.enrolled`,
          meta: { group: agreement.group.name },
        });
      }
    }

    (agreement.payments || []).forEach((payment, paymentIndex) => {
      if (!payment || !payment.date) return;
      const pId = payment.id || `${aId}-p${paymentIndex}`;
      const amount =
        payment.amountAgreement != null
          ? `${payment.amountAgreement} ${payment.currency || ''}`.trim()
          : '';

      events.push({
        id: `${pId}-paid`,
        date: payment.date,
        category: 'paid',
        labelKey: `${I18N_PREFIX}.paid`,
        meta: { amount },
      });

      if (payment.fakturowniaInvoice) {
        const linked = Boolean(payment.contribution?.id);
        events.push({
          id: `${pId}-invoice`,
          date: payment.date,
          category: linked ? 'invoiceLinked' : 'invoiceIssued',
          labelKey: `${I18N_PREFIX}.${
            linked ? 'invoiceLinked' : 'invoiceIssued'
          }`,
          meta: { number: payment.fakturowniaInvoice.number || '' },
        });
      }
    });

    if (agreement.terminated && agreement.terminatedDate) {
      events.push({
        id: `${aId}-terminated`,
        date: agreement.terminatedDate,
        category: 'terminated',
        labelKey: `${I18N_PREFIX}.terminated`,
        meta: { title },
      });
    }
  });

  return events.sort((a, b) => toTime(b.date) - toTime(a.date));
};
